import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { useState } from "react"
import { Link } from 'react-router-dom'
import { Button } from "@/ui/button"
import { Input } from "@/ui/input"
import { Checkbox } from "@/ui/checkbox"
import { MaterialIcon } from "@/ui/material-icon"

import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/ui/form"

import styles from "./DynamicForm.module.scss"

// ---------------- TYPES ----------------

interface FormFieldType {
    id: string
    label: string
    type: string
    placeholder?: string
    required?: boolean
    isDisplay?: boolean
    defaultValue?: any
    validation?: {
        pattern?: string
        minLength?: number
        maxLength?: number
        message?: string
    }
}

interface FormOptions {
    showForgotPassword?: boolean
    forgotPasswordLink?: string
    showBackToLogin?: boolean
    signupText?: string
    loginLink?: string
}

interface ActionButton {
    label: string
    onClick?: () => void
}

interface DynamicFormProps {
    fields: FormFieldType[]
    onSubmit: (data: any) => void
    primaryAction?: ActionButton
    secondaryAction?: ActionButton
    options?: FormOptions
    loading?: boolean
}

// ---------------- COMPONENT ----------------

const DynamicForm = ({
    fields,
    onSubmit,
    primaryAction,
    secondaryAction,
    options,
    loading,
}: DynamicFormProps) => {
    const [showPassword, setShowPassword] = useState(false)

    const displayedFields = fields.filter((f) => f.isDisplay)

    // 🔥 Dynamic Zod Schema
    const schema = z.object(
        displayedFields.reduce((acc, field) => {
            let validator: z.ZodTypeAny

            if (field.type === "checkbox") {
                validator = z.boolean()
            } else {
                let stringValidator = z.string()

                if (field.validation?.pattern) {
                    stringValidator = stringValidator.regex(
                        new RegExp(field.validation.pattern),
                        field.validation.message
                    )
                }

                if (field.validation?.minLength) {
                    stringValidator = stringValidator.min(
                        field.validation.minLength,
                        field.validation.message
                    )
                }

                if (field.validation?.maxLength) {
                    stringValidator = stringValidator.max(
                        field.validation.maxLength,
                        field.validation.message
                    )
                }

                if (field.required) {
                    stringValidator = stringValidator.min(
                        1,
                        `${field.label} is required`
                    )
                }

                validator = stringValidator
            }

            acc[field.id] = validator
            return acc
        }, {} as Record<string, z.ZodTypeAny>)
    )

    type FormData = z.infer<typeof schema>

    const form = useForm<FormData>({
        resolver: zodResolver(schema),
        defaultValues: displayedFields.reduce((acc, field) => {
            acc[field.id as keyof FormData] =
                field.type === "checkbox" ? field.defaultValue || false : ""
            return acc
        }, {} as any),
    })

    // ---------------- RENDER ----------------

    return (
        <Form {...form}>
            <form
                onSubmit={form.handleSubmit(onSubmit)}
                className={styles.loginForm}
            >
                {displayedFields.map((field) => (
                    <FormField
                        key={field.id}
                        control={form.control}
                        name={field.id}
                        render={({ field: f }) => (
                            <FormItem>
                                <FormLabel>{field.label}</FormLabel>

                                <FormControl>
                                    {field.type === "checkbox" ? (
                                        <Checkbox
                                            checked={!!f.value}
                                            onCheckedChange={f.onChange}
                                        />
                                    ) : field.type === "password" ? (
                                        <div className={styles.passwordField}>
                                            <Input
                                                type={showPassword ? "text" : "password"}
                                                placeholder={field.placeholder}
                                                {...f}
                                                className={styles.passwordInput}
                                            />
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="icon"
                                                className={styles.passwordToggle}
                                                onClick={() =>
                                                    setShowPassword((prev) => !prev)
                                                }
                                            >
                                                {showPassword ? (
                                                    <MaterialIcon name="visibility_off" size={16} />
                                                ) : (
                                                    <MaterialIcon name="visibility" size={16} />
                                                )}
                                            </Button>
                                        </div>
                                    ) : (
                                        <Input
                                            type={field.type}
                                            placeholder={field.placeholder}
                                            {...f}
                                        />
                                    )}
                                </FormControl>

                                <FormMessage />
                            </FormItem>
                        )}
                    />
                ))}

                {/* Forgot Password */}
                {options?.showForgotPassword && (
                    <Link
                        to={options.forgotPasswordLink || "/forgot-password"}
                        className={styles.forgotPassword}
                    >
                        Forgot your password?
                    </Link>
                )}

                {/* Primary Button */}
                <Button
                    type="submit"
                    disabled={loading || form.formState.isSubmitting}
                    className={styles.submitButton}
                >
                    {loading
                        ? "Processing..."
                        : primaryAction?.label || "Submit"}
                </Button>

                {/* Secondary Button */}
                {secondaryAction && (
                    <Button
                        type="button"
                        variant="outline"
                        onClick={secondaryAction.onClick}
                        className={styles.submitButton}
                    >
                        {secondaryAction.label}
                    </Button>
                )}

                {/* Back to login */}
                {options?.showBackToLogin && (
                    <Link
                        to={options.loginLink || "/login"}
                        className={styles.loginLink}
                    >
                        <MaterialIcon name="arrow_back" size={20} />
                        Back to Login
                    </Link>
                )}
            </form>
        </Form>
    )
}

export default DynamicForm